import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import ReactGA from 'react-ga4';
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";

import CustomCheckbox from "../../uiKit/CustomCheckbox/customCheckbox";
import { RadioIcon, RadioIconChecked } from "../../uiKit/assets/svg/icons";
import CustomInput from "../../uiKit/Input/CustomInput";
import Title from "../../uiKit/Title/Title";
import CustomButton from "../../uiKit/Button/CustomButton";
import { gray600 } from "../../uiKit/assets/colors/colors";
import CustomRadio from "../../uiKit/CustomRadio/customRadio";
import { DONATION_FEE, FUND_INFO, PAYMENT_METHOD, RECURRING_DATA } from "../../helpers/constants";
import Chariot from "./chariot";
import { onlyNumbers } from "../../helpers/validation";

import { changeIsAnonymous } from "../../redux/state/paymentSlice";
import { setDonationBody, setDonationFormOpen, setStripeOpen } from "../../redux/state/donationSlice";
import UnauthorizedUserDonateInfo, { SignInComponent } from "../UnauthorizedPages/donate";

const PredefinedOptions = [100, 54, 36, 18];

const DonationForm = ({ hasRecurring = false }) => {

    const dispatch = useDispatch();
    const { search } = useLocation();
    const { campaignId, communityId } = useParams();
    const query = new URLSearchParams(search);
    const paramsCampaignId = query.get("campaignId");

    const email = useSelector(state => state.account.userDetails.email, shallowEqual);
    const isAnonymous = useSelector(state => state.payment.isAnonymous, shallowEqual);
    const userDetails = useSelector(state => state.account.userDetails, shallowEqual);
    const unauthorizedUserInfo = useSelector(state => state.donation.body.userInfo, shallowEqual);
    const userInfoError = useSelector(state => state.donation.userInfoError, shallowEqual);

    const hasUserData = !!userDetails?.id || !userInfoError;

    const [amount, setAmount] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('1');
    const [error, setError] = useState('');
    const [coverFees, setCoverFees] = useState(true);
    const [savePaymentMethod, setSavePaymentMethod] = useState(true);
    const [recurring, setRecurring] = useState(1);
    const [disableRadio, setDisableRadio] = useState(true);

    const selectPaymentMethodHandler = (_, value) => {
        setPaymentMethod(value);
    }

    const predefinedOptionsHandler = (amount) => {
        setAmount(prev => prev + amount)
    }

    const closeFormHandler = () => dispatch(setDonationFormOpen(false));

    const onPayClick = () => {
        ReactGA.event('donate_campaign_enter_amount');
        closeFormHandler();
        dispatch(setStripeOpen(true))
    }

    const onChangeHandler = (e) => {
        const value = e.target.value;
        if (onlyNumbers(value)) {
            if (value === '0') {
                setError('Insufficient amount');
                setAmount(null);
            } else {
                setError('');
                setAmount(parseInt(value));
            }
        } else {
            setError('Required');
            setAmount(null);
        }
    }

    const anonymousHandler = (event) => {
        const value = event.target.checked;
        dispatch(changeIsAnonymous(value));
    }

    useEffect(() => {
        const userInfo = !!userDetails?.id ? { email } : unauthorizedUserInfo;

        const incomingBalanceId = !!campaignId ?
            campaignId :
            !!communityId ?
                communityId :
                !!paramsCampaignId ?
                    paramsCampaignId :
                    null;

        if (!incomingBalanceId) return;
        const body = {
            incomingBalanceId,
            amount,
            isAnonymous,
            userInfo,
            savePaymentMethod,
            coverFees,
            campaignId,
            donationPeriod: !disableRadio ? recurring : null,
        }
        dispatch(setDonationBody(body));
    }, [
        communityId,
        amount,
        isAnonymous,
        email,
        unauthorizedUserInfo,
        savePaymentMethod,
        coverFees,
        campaignId,
        dispatch,
        disableRadio,
        recurring,
        userDetails?.id,
        paramsCampaignId,
    ])

    return (
        <div className='donationWrapper'>
            <Title
                text='Donate'
                fontSize={32}
                lineHeight={40}
                style={{ marginBottom: '16px' }}
            />
            {
                !userDetails?.id && <SignInComponent />
            }
            {!!userDetails?.id && <RadioGroup
                aria-label="position"
                name="position"
                onChange={selectPaymentMethodHandler}
                value={paymentMethod}
                color='primary'
                style={{ marginBottom: '32px' }}
            >
                {
                    PAYMENT_METHOD.map((item, index) => (
                        <div
                            key={index}
                            style={{
                                borderRadius: "4px",
                                border: '1px solid  #DFDDDD',
                                width: '100%',
                                marginBottom: '8px',
                                marginLeft: 0,
                                padding: '0 10px',
                                display: 'flex',
                                justifyContent: "space-between",
                            }}
                        >
                            <FormControlLabel
                                value={item.id}
                                control={
                                    <Radio
                                        color='primary'
                                        icon={<RadioIcon />}
                                        checkedIcon={<RadioIconChecked />}
                                    />
                                }
                                label={item.value}
                            />
                            <img src={item.icon} alt={item.value} />
                        </div>
                    ))
                }
            </RadioGroup>}
            {
                !userDetails?.id && <UnauthorizedUserDonateInfo />
            }
            <CustomInput
                label={"Donation amount"}
                value={amount}
                onChange={onChangeHandler}
                error={!!error}
                errorMessage={error}
                placeholder='0'
                inputMode="numeric"
            />
            <div className="definedOptions">
                {
                    PredefinedOptions.map((x, index) => <div >
                        <Title text={`$ ${x}`} key={index} fontSize={12} onClick={() => predefinedOptionsHandler(x)} />
                    </div>)
                }
            </div>
            <CustomCheckbox
                label={DONATION_FEE}
                checked={coverFees}
                onChange={() => setCoverFees(!coverFees)}
            />
            {
                paymentMethod === "1" && <CustomCheckbox
                    label='Please save my credit card for future payments'
                    checked={savePaymentMethod}
                    onChange={() => setSavePaymentMethod(!savePaymentMethod)}
                    style={{ marginBottom: '16px' }}
                />
            }
            <CustomCheckbox
                checked={isAnonymous}
                onChange={anonymousHandler}
                label='Donate Anonymously'
                style={{ marginBottom: '16px' }}
            />
            {
                hasRecurring && <CustomRadio
                    label='Make this a recurring donation'
                    data={RECURRING_DATA}
                    checkBoxLabel={true}
                    onChange={(e) => setRecurring(parseInt(e.target.value))}
                    value={recurring}
                    style={{ marginLeft: '24px', marginBottom: '16px' }}
                    disable={disableRadio}
                    onCheckboxChange={() => setDisableRadio(!disableRadio)}
                />
            }
            {paymentMethod === "1" ? <CustomButton
                title='Continue to Payment Details'
                style={{ marginBottom: '16px' }}
                disabled={!!error || !amount || !hasUserData}
                onClick={onPayClick}
            /> : <Chariot />
            }
            <CustomButton
                title={'Decline'}
                style={{ marginBottom: '16px' }}
                isWhite
                onClick={closeFormHandler}
            />
            <Title
                text="You'll be asked for your payment info on the next page."
                fontSize={12}
                lineHeight={14}
                fontWeight={400}
                color={gray600}
            />
            <Title
                text={FUND_INFO}
                overflowText={false}
                fontSize={12}
                lineHeight={14}
                fontWeight={400}
                style={{
                    marginTop: "16px",
                    marginBottom: "40px",
                }}
                color={gray600}
            />
        </div>
    )
}

export default DonationForm;